import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Stack, Typography } from '@mui/material';
import { getAuth, UserInfo } from 'firebase/auth';

import { IWhoami } from '@mopla/data-models';
import { useBreakpoints } from '@mopla/utils';

import {
	EditIcon,
	Footer,
	MainWithFooterTemplate,
	MplButton,
	TrashIconSmall,
} from '../../';

import { EmailChangeModals } from './EmailEdit';
import { TEmailChangeResult } from './ProfileManager';
import {
	MyAccountTitleWithButton,
	MyAccountWrapper,
} from './ProfileManager.styles';

export interface PersonalScreenProps {
	onEdit: () => void;
	onEditEmail: () => void;
	onDeleteAccount: () => void;
	currentUser?: IWhoami;
	onCloseModal: () => void;
	emailChangeModalType: TEmailChangeResult;
}

export const PersonalScreen: FC<PersonalScreenProps> = ({
	onEdit,
	onEditEmail,
	onDeleteAccount,
	currentUser,
	onCloseModal,
	emailChangeModalType,
}) => {
	const { t } = useTranslation('profile');
	const { isAboveMobile } = useBreakpoints();
	const auth = getAuth();

	const socialProviders = [
		'google.com',
		'facebook.com',
		'apple.com',
		'microsoft.com',
	];

	const content = (
		<MyAccountWrapper data-testid="personalScreen-myAccountWrapper">
			<MyAccountTitleWithButton>
				<Typography
					data-testid="personalScreen-title"
					variant="bodyTitle"
					color="neutral.100"
				>
					{t('header.personal_data')}
				</Typography>
				<IconButton data-testid="personalScreen-editButton" onClick={onEdit}>
					<EditIcon />
				</IconButton>
			</MyAccountTitleWithButton>

			<div>
				<Typography
					data-testid="personalScreen-userNameTitle"
					variant="body2"
					color="neutral.80"
				>
					{t('title.name')}
				</Typography>
				<Typography
					color="neutral.100"
					data-testid="personalScreen-userNameData"
				>
					{currentUser?.firstName} {currentUser?.lastName}
				</Typography>
			</div>

			<div>
				<Typography
					data-testid="personalScreen-addressTitle"
					variant="body2"
					color="neutral.80"
				>
					{t('title.adress')}
				</Typography>
				<Typography
					color="neutral.100"
					data-testid="personalScreen-addressData"
				>
					{`${currentUser?.address?.street} ${currentUser?.address?.streetNumber}`}{' '}
					<br />
					{`${currentUser?.address?.zipCode} ${currentUser?.address?.city}`}
				</Typography>
			</div>

			<div>
				<Typography
					data-testid="personalScreen-phoneText"
					variant="body2"
					color="neutral.80"
				>
					{t('title.cell_phone')}
				</Typography>
				<Typography color="neutral.100" data-testid="personalScreen-phoneData">
					{currentUser?.phone}
				</Typography>
			</div>

			<div>
				<Typography
					data-testid="personalScreen-birthText"
					variant="body2"
					color="neutral.80"
				>
					{t('title.birth')}
				</Typography>
				<Typography color="neutral.100" data-testid="personalScreen-birthData">
					{currentUser?.dateOfBirth}
				</Typography>
			</div>

			<div>
				<MyAccountTitleWithButton>
					<Typography
						data-testid="personalScreen-email"
						variant="bodyTitle"
						color="neutral.100"
					>
						{t('header.email')}
					</Typography>
					{!auth?.currentUser?.providerData?.some((providerData: UserInfo) =>
						socialProviders.includes(providerData.providerId)
					) && (
						<IconButton
							data-testid="personalScreen-editEmailButton"
							onClick={onEditEmail}
						>
							<EditIcon />
						</IconButton>
					)}
				</MyAccountTitleWithButton>

				<Typography color="neutral.100" data-testid="personalScreen-emailData">
					{currentUser?.email}
				</Typography>
			</div>

			{!!emailChangeModalType && (
				<EmailChangeModals
					onCloseModal={onCloseModal}
					emailChangeModalType={emailChangeModalType}
					onBackToEmailEdit={() => {
						onCloseModal();
						onEditEmail();
					}}
				/>
			)}
		</MyAccountWrapper>
	);

	if (isAboveMobile) {
		return content;
	}

	return (
		<MainWithFooterTemplate
			content={content}
			footer={
				<Footer>
					<Stack alignItems="center">
						<MplButton
							testId="personalScreen-deleteAccountButton"
							variant="text"
							color="error"
							onClick={onDeleteAccount}
							endIcon={<TrashIconSmall />}
						>
							{t('button.delete_account')}
						</MplButton>
					</Stack>
				</Footer>
			}
		/>
	);
};
